import {
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  Renderer2,
  inject
} from '@angular/core';
import { Strategy, Trade } from '@approot/models';
import { Header } from '@approot/models/ag-grid.model';
import { ProviderQuoteEvent } from '@approot/provider-quote/provider-quote.model';
import {
  ComponentVisibilityService,
  ConfigService,
  NotificationService,
  RfqService,
  StrategiesService,
  StrategyDetailService,
  TradesCaptureService,
  TradesService,
  UserStorageService
} from '@approot/services';
import { SubscriptionSink, isFeatureEnabled, sleep } from '@approot/utilities';
import { RfqTimelineService } from '@approot/widgets/rfq/services/rfq-timeline.service';
import { Observable, of } from 'rxjs';
import { map, switchMap, take } from 'rxjs/operators';

@Component({
  selector: 'app-positions-detail',
  templateUrl: './positions-detail.component.html',
  styleUrls: ['./positions-detail.component.scss']
})
export class PositionsDetailComponent implements OnInit, OnDestroy {
  public tradesEnabled: boolean;
  public loginState$: Observable<boolean>;
  public trades = false;
  public blotter = false;
  public showRfqTab = false;
  public tabSelected = false;
  public showTrades = false;
  public showPrivateTrades = true;
  public showBlotter = false;
  public showEventsBlotter = false;
  public showRisksBlotter = false;
  public showPNLBlotter = false;
  public showCashFlowsBlotter = false;
  public hasTradesBeenClicked = false;
  public enableWhiteLabelTradingRules = false;
  public showWhiteLabelBlotter = false;
  public showDeltaHedges = false;
  public isBlotterAccessAuthorized: Observable<boolean> = of(false);
  public isEventsBlotterAccessAuthorized: Observable<boolean> = of(false);
  public isCashFlowsBlotterAccessAuthorized: Observable<boolean> = of(false);
  public isRisksBlotterAccessAuthorized: Observable<boolean> = of(false);
  public isPnlBlotterAccessAuthorized: Observable<boolean> = of(false);
  public isLifecycleBlotterAccessAuthorized: Observable<boolean> = of(false);
  private subscriptions = new SubscriptionSink();
  private strategyDetailService = inject(StrategyDetailService);
  private notificationService = inject(NotificationService);

  constructor(
    private tradesService: TradesService,
    private strategiesService: StrategiesService,
    private componentVisibilityService: ComponentVisibilityService,
    private tradeCaptureService: TradesCaptureService,
    private rfqService: RfqService,
    private elementRef: ElementRef,
    private renderer: Renderer2,
    private configService: ConfigService,
    private userStorageService: UserStorageService,
    private rfqTimelineService: RfqTimelineService
  ) {}

  public ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  public ngOnInit() {
    this.subscriptions.sink =
      this.tradeCaptureService.tradeCaptureTablesLoaded.subscribe(
        tradeTablesLoaded => {
          this.tradesEnabled =
            !!this.tradeCaptureService.dvEntities?.enableTrades;

          if (tradeTablesLoaded && !this.tabSelected) {
            // if the trades are loaded and no tab has already been selected
            // select the trades tab
            this.showRfqTab = false;
            this.showPrivateTrades = false;
            this.showBlotter = false;
            this.showEventsBlotter = false;
            this.showTrades = true;
          }
        }
      );

    this.subscriptions.sink = this.configService
      .getUserSettings()
      .subscribe(settings => {
        this.enableWhiteLabelTradingRules =
          settings.enableWhiteLabelTradingRules;
      });

    this.subscribeToEvents();
    this.loginState$ = this.rfqService.loginState$;
    const settings = this.configService.getUserSettings();

    this.isBlotterAccessAuthorized = settings.pipe(
      map(r => r.isBlotterAccessAuthorized)
    );

    this.isEventsBlotterAccessAuthorized = settings.pipe(
      map(r => r.isEventsBlotterAccessAuthorized)
    );

    this.isCashFlowsBlotterAccessAuthorized = settings.pipe(
      map(r => r.isCashFlowsBlotterAccessAuthorized)
    );

    this.isRisksBlotterAccessAuthorized = settings.pipe(
      map(r => r.isRisksBlotterAccessAuthorized)
    );

    this.isPnlBlotterAccessAuthorized = settings.pipe(
      map(r => r.isPnlBlotterAccessAuthorized)
    );

    this.isLifecycleBlotterAccessAuthorized = settings.pipe(
      map(r => r.isLifeCyclingBlotterAccessAuthorized)
    );

    settings
      .pipe(
        map(r => {
          this.rfqTimelineService.setAgGridRfqTimelineDefaultHeaders(
            r.ag_grid_config
              .find(config => config.identifier === 'defaultRfqTimelineHeaders')
              ?.headers.map((header: string | Header) => ({ field: header })) ||
              []
          );
        }),
        take(1)
      )
      .subscribe();
  }

  private subscribeToEvents() {
    this.subscriptions.sink = this.rfqService.events$
      .pipe(
        map(evt => evt.filter(row => row['@type'] === 'ProviderQuoteEvent')),
        map((row: ProviderQuoteEvent[]) => row.map(r => r.action))
      )
      .subscribe(quoteEvent => {
        if (
          quoteEvent.indexOf('SUBMITTED') !== -1 ||
          quoteEvent.indexOf('ClientAccept') !== -1
        ) {
          this.pulseIncomingRfq(quoteEvent);
        }
      });
  }

  // load the selected strategy into UI legs
  onLoadStrategies(strategy: Strategy) {
    strategy.products.forEach((product: any) => {
      this.strategyDetailService.addLegToStructuringScreen(
        product.productType,
        false,
        product,
        this.strategyDetailService.tradingMode
      );
    });
  }

  // strategies are radio button (single select)
  onDeleteStrategy(strategy: Strategy) {
    // pass in the dynamodb partition key (userId) and sort key (timestamp)
    this.strategiesService.deleteStrategy(strategy.timestamp);
  }

  // trades are checkbox (multi select)
  onDeleteTrades(trades: Trade[]) {
    trades.forEach(trade => {
      // pass in the dynamodb partition key (userId) and sort key (timestamp)
      this.tradesService.deleteTrade(trade.userId, trade.timestamp);
    });
  }

  onLoadTrades(trades: Record<string, any>[]) {
    trades.forEach((trade: Trade) => {
      this.strategyDetailService.addLegToStructuringScreen(
        trade.productType,
        false,
        trade,
        this.strategyDetailService.tradingMode
      );
    });
  }

  public getRfqTimeline(requestId: number): void {
    this.rfqTimelineService.fetchRfqTimeline(requestId);
  }

  closePosition(val: boolean) {
    return this.componentVisibilityService.updatePositionGrid(val);
  }

  get showRFQs(): boolean {
    return this.rfqService.currentUserRole() === 'ProviderTrader';
  }

  private async pulseIncomingRfq(quoteEvent: string[]) {
    const elem = this.elementRef.nativeElement.querySelector(
      '.incoming-rfq__btn'
    ) as HTMLElement;
    if (!elem) {
      return;
    }

    if (
      isFeatureEnabled('enableDesktopNotifications', false) &&
      quoteEvent.indexOf('SUBMITTED') !== -1
    ) {
      this.notificationService
        .requestPermission()
        .pipe(
          switchMap(() => {
            console.log('Incoming RFQ', quoteEvent);
            return this.notificationService.open('Incoming RFQ', {
              body: 'There is an incoming RFQ',
              requireInteraction: false
            });
          })
        )
        .subscribe();
    }

    this.userStorageService.ping();
    this.renderer.removeClass(elem, 'pulse');
    await sleep(100);
    this.renderer.addClass(elem, 'pulse');
  }
}
